import type { ReactNode } from 'react'
import s from './Timeline.module.scss'

/** Richtung der Wirkung. Bestimmt die Farbe des Balkens. */
export type TimelineTone = 'good' | 'bad' | 'neutral' | 'unclear'

export function Timeline({ children }: { children: ReactNode }) {
  return <div className={s.timeline}>{children}</div>
}

interface EntryProps {
  /** Wann es geschah. Links in fester Spalte, damit die Einträge fluchten. */
  date: ReactNode
  /**
   * Wann wir es erfuhren — nur wenn es ein anderer Tag war.
   *
   * Für eine Rückschau der maßgebliche Zeitpunkt: Vorher wussten wir es nicht.
   * Steht klein unter dem Datum, weil es die Ausnahme ist.
   */
  recorded?: ReactNode
  tone?: TimelineTone
  headline: ReactNode
  /** Art, Bedeutung, Verschiebung der Wahrscheinlichkeit — eine graue Zeile. */
  meta?: ReactNode
  /** Der Sachverhalt. */
  children?: ReactNode
  /** Die Einordnung eines Menschen. Kursiv, sichtbar getrennt vom Sachverhalt. */
  comment?: ReactNode
}

/**
 * Ein Eintrag im Verlauf.
 *
 * Der farbige Balken links trägt die Richtung: Wer die Liste überfliegt, sieht
 * ohne zu lesen, ob es für den Titel gut oder schlecht lief. `unclear` ist
 * ausdrücklich eine eigene Farbe und nicht `neutral` — „lässt sich noch nicht
 * einordnen" ist etwas anderes als „ohne Wirkung".
 */
export function TimelineEntry({ date, recorded, tone = 'neutral', headline,
                               meta, children, comment }: EntryProps) {
  return (
    <div className={s.entry}>
      <div className={s.when}>
        {date}
        {recorded && <div className={s.recorded}>erfasst {recorded}</div>}
      </div>
      <div className={s.body} data-tone={tone}>
        <div className={s.headline}>{headline}</div>
        {meta && <div className={s.meta}>{meta}</div>}
        {children && <p className={s.fact}>{children}</p>}
        {comment && <p className={s.comment}>{comment}</p>}
      </div>
    </div>
  )
}
